import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IssuedKitItem, Station } from '@hcc/types';
import {
  useKitDraftQuery,
  useSaveKitDraft,
  useDiscardKitDraft,
  type KitDraftPayload,
  type KitDraftSeed,
} from '../../hooks/useKitDrafts';
import { ApiError } from '../../lib/api';
import type { ConfirmRow } from './useKitConfirmItems';

/**
 * Loads any saved draft for this person + station and hands the drawer's
 * state hooks their starting point. Seeding waits on this deliberately: the
 * alternative is a flash of template state that then jumps to the draft,
 * which at a desk reads as the app losing someone's work.
 */
export function useKitDraftLoad(personId: string, station: Station) {
  const query = useKitDraftQuery(personId, station);
  const [nonce, setNonce] = useState(0);
  // Minted once per drawer opening and only used when there is no draft to
  // inherit one from, so every desk that picks this up shares a key.
  const freshEventId = useMemo(() => crypto.randomUUID(), []);

  const envelope = query.data?.draft ?? null;
  const ready = query.isFetchedAfterMount && !query.isFetching;

  const seed: KitDraftSeed = {
    ready,
    nonce,
    ...(envelope
      ? {
          items: envelope.draft.items,
          assetCategoryById: envelope.draft.asset_category_by_id,
          confirm: envelope.draft.confirm,
        }
      : {}),
  };

  const reload = useCallback(async () => {
    await query.refetch();
    setNonce((n) => n + 1);
  }, [query]);

  return {
    seed,
    /** Whoever last saved this draft; null when there is nothing to pick up. */
    pickedUpFrom: envelope?.updated_by_name ?? null,
    pickedUpAt: envelope?.updated_at ?? null,
    pickedUpBy: envelope?.updated_by ?? null,
    /** Desk notes from the draft; the drawer owns the state so it seeds them. */
    notes: envelope?.draft.notes,
    version: envelope?.version,
    clientEventId: envelope?.draft.client_event_id ?? freshEventId,
    reload,
  };
}

export type KitDraftLoad = ReturnType<typeof useKitDraftLoad>;

export type DraftSaveState = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

interface AutosaveInput {
  personId: string;
  station: Station;
  /** Gate: no saving until the drawer has seeded from the loaded draft. */
  ready: boolean;
  items: IssuedKitItem[];
  assetCategoryById: Record<string, string | null>;
  confirmRows: ConfirmRow[];
  notes: string;
  clientEventId: string;
  /** Version loaded at open; undefined when there was no draft. */
  initialVersion?: number;
  /** Bumped by the loader on every re-seed; resets the save baseline. */
  seedNonce: number;
}

/** Strip the drawer's live objects down to what is worth persisting. */
function buildPayload(input: AutosaveInput): KitDraftPayload {
  return {
    items: input.items,
    asset_category_by_id: input.assetCategoryById,
    // Photos are omitted on purpose (see KitDraftPayload); the asset itself
    // is live data re-fetched on load, so only the desk's edits travel.
    confirm: input.confirmRows.map((r) => ({
      asset_id: r.asset.id,
      outcome: r.outcome,
      ...(r.serial ? { serial: r.serial } : {}),
      ...(r.registration ? { registration: r.registration } : {}),
      ...(r.last_service_date ? { last_service_date: r.last_service_date } : {}),
      ...(r.attributes && Object.keys(r.attributes).length > 0
        ? { attributes: r.attributes }
        : {}),
    })),
    notes: input.notes,
    client_event_id: input.clientEventId,
  };
}

/**
 * Debounced autosave of the drawer's working state, mirroring the training
 * and form builders: 700ms after the last edit, plus a flush on close so
 * walking away mid-item doesn't lose the last few keystrokes.
 *
 * Saves are content-compared rather than change-counted, which is what keeps
 * the seed itself (and photo-only edits, which aren't persisted) from
 * generating pointless writes.
 */
export function useKitDraftAutosave(input: AutosaveInput) {
  const [saveState, setSaveState] = useState<DraftSaveState>('idle');
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const save = useSaveKitDraft(input.personId);
  const discard = useDiscardKitDraft(input.personId);

  const versionRef = useRef<number | undefined>(input.initialVersion);
  const lastSavedRef = useRef<string | null>(null);
  const submittedRef = useRef(false);
  const haltedRef = useRef(false);
  const latestRef = useRef<KitDraftPayload | null>(null);

  const payload = input.ready ? buildPayload(input) : null;
  const serialised = payload ? JSON.stringify(payload) : null;
  latestRef.current = payload;

  // Every re-seed starts over: discarding a draft, or loading the version a
  // colleague saved, both re-run the drawer's seeding and must re-baseline.
  // `ready` is false while that happens, so the baseline effect below can
  // only fire once the new state has settled.
  useEffect(() => {
    lastSavedRef.current = null;
    versionRef.current = undefined;
    haltedRef.current = false;
    submittedRef.current = false;
    setConflictMessage(null);
    setSaveState('idle');
  }, [input.seedNonce]);

  // The first payload seen after seeding is the baseline, not an edit: it is
  // either the draft we just loaded or the untouched template.
  useEffect(() => {
    if (!input.ready || lastSavedRef.current !== null || serialised === null) return;
    lastSavedRef.current = serialised;
    // A version already exists only when a draft was loaded.
    versionRef.current = input.initialVersion;
  }, [input.ready, serialised, input.initialVersion]);

  const persist = useCallback(async () => {
    const current = latestRef.current;
    if (!current || haltedRef.current || submittedRef.current) return;
    const body = JSON.stringify(current);
    if (body === lastSavedRef.current) return;
    try {
      const res = await save.mutateAsync({
        station: input.station,
        draft: current,
        ...(versionRef.current !== undefined ? { version: versionRef.current } : {}),
      });
      versionRef.current = res.draft.version;
      lastSavedRef.current = body;
      setSaveState('saved');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Someone else saved while this drawer was open. Stop writing rather
        // than fight them for the row; the banner offers to load their copy.
        haltedRef.current = true;
        setConflictMessage(err.message);
        setSaveState('conflict');
        return;
      }
      setSaveState('error');
    }
    // save.mutateAsync is stable enough for this use; including it would
    // re-arm the debounce on every mutation state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.station]);

  useEffect(() => {
    if (!input.ready || serialised === null) return;
    if (serialised === lastSavedRef.current) return;
    if (haltedRef.current || submittedRef.current) return;
    setSaveState('saving');
    const t = setTimeout(() => {
      void persist();
    }, 700);
    return () => clearTimeout(t);
  }, [serialised, input.ready, persist]);

  // Closing the drawer mid-debounce must not drop the last edits.
  useEffect(() => {
    return () => {
      if (submittedRef.current || haltedRef.current) return;
      void persist().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Call once the kit has been signed for: the server clears the draft. */
  const markSubmitted = useCallback(() => {
    submittedRef.current = true;
  }, []);

  /**
   * Throw the draft away without issuing (wrong person, abandoned). The
   * caller re-seeds afterwards, and the seedNonce reset above is what puts
   * saving back on a clean baseline rather than writing the template back.
   */
  const discardDraft = useCallback(async () => {
    // Suppress the debounce and the unmount flush while the delete is in
    // flight, or either could recreate the row we are removing.
    submittedRef.current = true;
    await discard.mutateAsync(input.station);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.station]);

  return {
    saveState,
    conflictMessage,
    markSubmitted,
    discardDraft,
    isDiscarding: discard.isPending,
  };
}
